import { FGSelectInput, FieldGroup, FGTextAreaInput, FGTextInput, FGCheckboxInput } from "nsitools-react";
import * as React from "react";
import { useHistory } from "react-router";
import * as Yup from "yup";

import { SubActivityApi, SubActivityTranslationDto, SubActivityDetailDto } from "../../../../api";
import { ERoutes } from "../../../../ERoutes";
import { SmallFormGenerator, FGMultiTranslate, FGSRSHCheckboxInput } from "../../../../components";
import { useGlobalData } from "../../../../contexts";
import { useApiService, useCrudApi, useTl } from "../../../../hooks";
import { useReferential } from "../../../../hooks/useReferential";
import { ETLCodes } from "../../../../locales";

export interface ISubActivityDetailPageProps {
  idSubActivity: number;
  editMode: boolean;
}

export const SubActivityDetailPage: React.FunctionComponent<ISubActivityDetailPageProps> = ({
  idSubActivity,
  editMode,
}) => {
  const { t } = useTl();
  const api = useApiService(SubActivityApi);
  const history = useHistory();
  const { languages } = useGlobalData();
  const { currentLanguage } = useGlobalData();
  const [activities, aLoading] = useReferential((a) => a.getActivities(0), true, [currentLanguage]);
  const { data, loading, deleteItem, deleting, saveItem, saving } = useCrudApi<SubActivityDetailDto>(
    {
      getApiFn: (id = idSubActivity) =>
        id > 0
          ? api.get(id)
          : new SubActivityDetailDto({
              isCac: false,
              subActivityTranslations: [
                ...languages.map(
                  (l) =>
                    new SubActivityTranslationDto({
                      languageId: l.languageId,
                    })
                ),
              ],
            }).toJSON(),
      saveApiFn: (d: SubActivityDetailDto) => api.save(d),
      onSavedRoute: (d) => `${ERoutes.subActivity}/${d.subActivityId}/detail/edit`,
      deleteApiFn: (d) => api.delete(d.subActivityId),
      onDeletedRoute: () => ERoutes.subActivity,
    },
    [idSubActivity]
  );

  const FormSchema = React.useMemo(() => {
    return Yup.object().shape({
      activityId: Yup.number().nullable().required(t(ETLCodes.Required)),
      subActivityTranslations: Yup.array().of(
        Yup.object().shape({
          text: Yup.string().nullable().required(t(ETLCodes.Required)),
        })
      ),
    });
  }, [t]);

  return (
    data && (
      <SmallFormGenerator
        initialValues={data}
        onSubmit={saveItem}
        editMode={editMode}
        validationSchema={FormSchema}
        loading={loading}
        onCancel={() => history.push(ERoutes.subActivity)}
        onDelete={deleteItem}
        showDeleteButton={idSubActivity > 0}
        saving={saving}
        deleting={deleting}
      >
        <FieldGroup>
          <FGSelectInput name="activityId" label={t(ETLCodes.Activity)} items={activities} loading={aLoading} />
          <FGSRSHCheckboxInput name="clubMatch" label={t(ETLCodes.ClubMatch)} />
          <FGSRSHCheckboxInput name="isCac" label={t(ETLCodes.IsCac)} readonly />
          <FGSRSHCheckboxInput name="fciTax" label={t(ETLCodes.FciTax)} />
          <FGSRSHCheckboxInput name="participationFciTax" label={t(ETLCodes.ParticipationFciTax)} />
          <FGTextInput name="showType" label={t(ETLCodes.ShowType)} readonly />
          <FGCheckboxInput name="showOnPortal" label={t(ETLCodes.ShowOnPortal)} />
        </FieldGroup>
        <FieldGroup>
          <FGMultiTranslate
            label={ETLCodes.Name}
            translationsArrayField="subActivityTranslations"
            translationField="text"
            translationDto={SubActivityTranslationDto}
          />
          <FGTextAreaInput name="remark" label={t(ETLCodes.Remark)} />
        </FieldGroup>
      </SmallFormGenerator>
    )
  );
};
